import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, Table, Button, Input, Modal, Form, Switch, InputNumber, Space, message, Typography, Tag, Popconfirm, Tooltip, Spin, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { suggestedQueryService, SuggestedQuery } from '../services/suggestedQueryService';

const { Text } = Typography;

const MAX_QUERIES = 10;
type LanguageCode = 'en' | 'ga';

const languageLabels: Record<LanguageCode, string> = {
  en: 'English',
  ga: 'Irish',
};

const SuggestedQueriesManagement: React.FC = () => {
  const [queries, setQueries] = useState<SuggestedQuery[]>([]);
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingQuery, setEditingQuery] = useState<SuggestedQuery | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>('en');
  const [form] = Form.useForm();

  const activeCount = useMemo(
    () => queries.filter(q => q.is_active).length,
    [queries]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const allQueries = await suggestedQueryService.getAllQueries(showInactive, selectedLanguage);
      setQueries(allQueries);
    } catch {
      message.error('Failed to load suggested queries');
    } finally {
      setLoading(false);
    }
  }, [selectedLanguage, showInactive]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = () => {
    setEditingQuery(null);
    form.resetFields();
    form.setFieldsValue({ sort_order: queries.length, language_code: selectedLanguage });
    setModalOpen(true);
  };

  const handleEdit = (query: SuggestedQuery) => {
    setEditingQuery(query);
    form.setFieldsValue({
      text: query.text,
      language_code: query.language_code || 'en',
      sort_order: query.sort_order,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingQuery) {
        await suggestedQueryService.updateQuery(editingQuery.id, values);
        message.success('Query updated');
      } else {
        await suggestedQueryService.createQuery(values);
        message.success('Query created');
      }
      setModalOpen(false);
      fetchData();
    } catch (err: unknown) {
      if (axios.isAxiosError<{ detail?: string }>(err) && err.response?.data.detail) {
        message.error(err.response.data.detail);
      }
    }
  };

  const handleDelete = async (queryId: string) => {
    try {
      await suggestedQueryService.deleteQuery(queryId);
      message.success('Query deleted');
      fetchData();
    } catch {
      message.error('Failed to delete query');
    }
  };

  const handleToggle = async (queryId: string) => {
    try {
      await suggestedQueryService.toggleQuery(queryId);
      fetchData();
    } catch (err: unknown) {
      const detail = axios.isAxiosError<{ detail?: string }>(err)
        ? err.response?.data.detail
        : undefined;
      message.error(detail || 'Failed to toggle query status');
    }
  };

  const columns: ColumnsType<SuggestedQuery> = [
    {
      title: 'Order',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80,
      sorter: (a: SuggestedQuery, b: SuggestedQuery) => a.sort_order - b.sort_order,
      defaultSortOrder: 'ascend' as const,
    },
    {
      title: 'Question Text',
      dataIndex: 'text',
      key: 'text',
      render: (text: string) => (
        <Text style={{ maxWidth: 500 }} ellipsis={{ tooltip: text }}>
          {text}
        </Text>
      ),
    },
    {
      title: 'Language',
      dataIndex: 'language_code',
      key: 'language_code',
      width: 100,
      render: (languageCode: 'en' | 'ga') => (
        <Tag color={languageCode === 'ga' ? 'blue' : 'geekblue'}>
          {languageCode === 'ga' ? 'GA' : 'EN'}
        </Tag>
      ),
      filters: [
        { text: 'EN', value: 'en' },
        { text: 'GA', value: 'ga' },
      ],
      onFilter: (value, record) => record.language_code === value,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'default'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_value: unknown, record: SuggestedQuery) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title={record.is_active ? 'Deactivate' : 'Activate'}>
            <Switch
              size="small"
              checked={record.is_active}
              onChange={() => handleToggle(record.id)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this query?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading && queries.length === 0) {
    return (
      <div className="admin-content-container">
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content-container">
      <div className="page-header-row">
        <h2 className="page-title">Suggested Queries</h2>
        <Space>
          <Select<LanguageCode>
            aria-label="Suggested-query language"
            value={selectedLanguage}
            onChange={setSelectedLanguage}
            options={[
              { value: 'en', label: 'English (EN)' },
              { value: 'ga', label: 'Irish (GA)' },
            ]}
            style={{ width: 140 }}
          />
          <Text type="secondary" style={{ fontSize: 13 }}>
            {`${activeCount}/${MAX_QUERIES} active in ${languageLabels[selectedLanguage]}`}
          </Text>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#8c8c8c' }}>Show inactive:</span>
            <Switch
              size="small"
              checked={showInactive}
              onChange={setShowInactive}
            />
          </div>
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            disabled={activeCount >= MAX_QUERIES}
          >
            Add Query
          </Button>
        </Space>
      </div>

      <Card>
        <Table
          dataSource={queries}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="middle"
          locale={{ emptyText: 'No suggested queries yet. Click "Add Query" to create one.' }}
        />
      </Card>

      <Modal
        title={editingQuery ? 'Edit Suggested Query' : 'Add Suggested Query'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText={editingQuery ? 'Update' : 'Create'}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="text"
            label="Question Text"
            rules={[
              { required: true, message: 'Please enter the question text' },
              { max: 200, message: 'Question must be under 200 characters' },
            ]}
          >
            <Input.TextArea
              rows={2}
              placeholder="e.g. What supports are available for first-time home buyers?"
              maxLength={200}
              showCount
            />
          </Form.Item>
          <Form.Item
            name="language_code"
            label="Language"
            rules={[{ required: true, message: 'Please select a language' }]}
          >
            <Select
              disabled={!editingQuery}
              options={[
                { value: 'en', label: 'English (en)' },
                { value: 'ga', label: 'Irish (ga)' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="sort_order"
            label="Display Order"
            rules={[{ required: true, message: 'Please enter a sort order' }]}
          >
            <InputNumber min={0} max={99} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SuggestedQueriesManagement;
